import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  fetchCategoryArchiveBySlug,
  fetchCategoryBundleBySlug,
  fetchCategorySeoBySlug,   // ← use the proxy-aware version from api/posts
  fetchRankMathTermSeo,
  fetchPosts,
  fetchPostsByIds,
  fetchTagsBySearch,
  getCategorySlugFromPath,
  isLatestUpdatesPath,
  LATEST_UPDATES_SLUG,
} from "../api/posts";
import PostGrid from "../components/PostGrid";
import SEO from "../components/SEO";
import { getPostImageData, getPostImageUrl } from "../utils/postMedia";

// ─────────────────────────────────────────────────────────────────────────────
// BOOKMARK UTILITIES
// ─────────────────────────────────────────────────────────────────────────────

const ARTICLE_BOOKMARKS_STORAGE_KEY      = "trw-article-bookmarks-v1";
const ARTICLE_BOOKMARKS_DATA_STORAGE_KEY = "trw-article-bookmarks-data-v1";

function readBookmarkedPostIds() {
  if (typeof window === "undefined") return [];
  try {
    const raw    = window.localStorage.getItem(ARTICLE_BOOKMARKS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map((v) => String(v)) : [];
  } catch { return []; }
}

function readBookmarkedPosts() {
  if (typeof window === "undefined") return [];
  try {
    const raw    = window.localStorage.getItem(ARTICLE_BOOKMARKS_DATA_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function writeBookmarkedPosts(posts) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(ARTICLE_BOOKMARKS_DATA_STORAGE_KEY, JSON.stringify(posts));
  window.dispatchEvent(new Event("storage"));
}

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS  (no hardcoded WP URLs here — all routing goes through api/posts)
// ─────────────────────────────────────────────────────────────────────────────

const POSTS_CACHE_TTL_MS                     = 10 * 60 * 1000;
const SEO_SITE_NAME                          = "Travel Rethink Ways";
const SEO_BASE_URL                           = "https://travel.rethinkways.com";
const DEFAULT_SEO_IMAGE                      = "https://travel.rethinkways.com/wp-content/uploads/2025/12/Travel_PR_iTour_Feat-02-1.jpg";
const LATEST_UPDATES_PAGE_TITLE              = "Latest Updates";
const SEARCH_RESULTS_PAGE_TITLE              = "Search Results";
const EXCLUDED_LATEST_UPDATES_KEYWORDS = [
  "job",
  "jobs",
  "hospitality",
];
const LOAD_MORE_THRESHOLD_PX                 = 1200;
const LATEST_UPDATES_PER_PAGE               = 100;

function getSearchQueryFromSearch(search = typeof window !== "undefined" ? window.location.search : "") {
  const params = new URLSearchParams(search);
  return params.get("q")?.trim() || params.get("s")?.trim() || "";
}

const INITIAL_PAGE_IS_LATEST_UPDATES = isLatestUpdatesPath();
const INITIAL_PAGE_IS_SEARCH = typeof window !== "undefined" && (
  /\/search\/?$/i.test(window.location.pathname) ||
  (window.location.pathname === "/" && Boolean(getSearchQueryFromSearch(window.location.search)))
);
const INITIAL_SEARCH_QUERY = typeof window !== "undefined"
  ? getSearchQueryFromSearch(window.location.search)
  : "";
const INITIAL_CATEGORY_SLUG = INITIAL_PAGE_IS_LATEST_UPDATES
  ? LATEST_UPDATES_SLUG
  : INITIAL_PAGE_IS_SEARCH
    ? `search:${INITIAL_SEARCH_QUERY.toLowerCase()}`
  : getCategorySlugFromPath();
const INITIAL_SEO = (!INITIAL_PAGE_IS_LATEST_UPDATES && !INITIAL_PAGE_IS_SEARCH)
  ? readCachedSeo(INITIAL_CATEGORY_SLUG)
  : null;
const INITIAL_POSTS_REQUEST = INITIAL_PAGE_IS_LATEST_UPDATES
  ? fetchPosts({ orderby: "date", order: "desc" }).then(filterLatestUpdatePosts)
  : INITIAL_PAGE_IS_SEARCH
    ? (INITIAL_SEARCH_QUERY
        ? fetchTagsBySearch(INITIAL_SEARCH_QUERY, { per_page: 50 }).then((tags) => {
            const tagIds = (Array.isArray(tags) ? tags : []).map((t) => t?.id).filter(Boolean);
            if (!tagIds.length) return [];
            return fetchPosts({ tags: tagIds.join(","), orderby: "date", order: "desc" }).then(filterSearchResultPosts);
          })
        : Promise.resolve([]))
  : fetchCategoryArchiveBySlug(INITIAL_CATEGORY_SLUG);

// ─────────────────────────────────────────────────────────────────────────────
// SEO PARSER
// api/posts.fetchCategorySeoBySlug already returns { category, title,
// description, canonicalUrl, image } from yoast_head_json.
// We extend it here to also handle Rank Math (rank_math_head raw HTML string)
// with a clean priority chain: Rank Math → Yoast → generic fallback.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Takes the raw category object returned by fetchCategorySeoBySlug and
 * resolves the best available SEO data.
 *
 * fetchCategorySeoBySlug in api/posts already reads yoast_head_json.
 * We check rank_math_head first (if present) then fall through to Yoast,
 * then to a plain fallback so nothing is ever undefined.
 */
function resolveCategorySeo(categorySeoResult, fallbackLabel) {
  // fetchCategorySeoBySlug returns null when the category is not found
  if (!categorySeoResult) return buildFallbackSeo(fallbackLabel, null);

  const { category } = categorySeoResult;

  // ── 1. Rank Math (rank_math_head — raw HTML string) ──────────────────────
  const rmHead = category?.rank_math_head;
  if (rmHead && typeof rmHead === "string" && rmHead.trim().length > 0) {
    const doc     = new DOMParser().parseFromString(
      `<html><head>${rmHead}</head></html>`,
      "text/html"
    );
    const getMeta = (attr, val) =>
      doc.head.querySelector(`meta[${attr}="${val}"]`)?.getAttribute("content") || null;
    const getLink = (rel) =>
      doc.head.querySelector(`link[rel="${rel}"]`)?.getAttribute("href") || null;

    const title     = doc.head.querySelector("title")?.textContent?.trim() || null;
    const desc      = getMeta("name", "description");
    const canonical = getLink("canonical");
    const ogTitle   = getMeta("property", "og:title");
    const ogDesc    = getMeta("property", "og:description");
    const ogImage   = getMeta("property", "og:image");
    const twImage   = getMeta("name", "twitter:image");

    if (title || desc || ogImage) {
      return {
        title:        title     || ogTitle   || `${fallbackLabel} | ${SEO_SITE_NAME}`,
        description:  desc      || ogDesc    || buildSeoDescription(fallbackLabel),
        canonicalUrl: canonical || category?.link || `${SEO_BASE_URL}/category/${category?.slug}/`,
        image:        ogImage   || twImage   || DEFAULT_SEO_IMAGE,
      };
    }
  }

  // ── 2. Yoast (already resolved by api/posts into flat fields) ────────────
  if (categorySeoResult.title || categorySeoResult.description) {
    return {
      title:        categorySeoResult.title        || `${fallbackLabel} | ${SEO_SITE_NAME}`,
      description:  categorySeoResult.description  || buildSeoDescription(fallbackLabel),
      canonicalUrl: categorySeoResult.canonicalUrl || category?.link || `${SEO_BASE_URL}/category/${category?.slug}/`,
      image:        categorySeoResult.image        || DEFAULT_SEO_IMAGE,
    };
  }

  // ── 3. Generic fallback ───────────────────────────────────────────────────
  return buildFallbackSeo(fallbackLabel, category);
}

function buildFallbackSeo(label, category) {
  return {
    title:        `${category?.name || label} | ${SEO_SITE_NAME}`,
    description:  category?.description || buildSeoDescription(label),
    canonicalUrl: category?.link || `${SEO_BASE_URL}/category/${category?.slug || label}/`,
    image:        DEFAULT_SEO_IMAGE,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// META TAG HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function ensureLinkTag(selector, attributes) {
  let el = document.head.querySelector(selector);
  if (!el) { el = document.createElement("link"); document.head.appendChild(el); }

  // Prevent invalid `<link ... href="undefined">` and similar warnings.
  const href = attributes?.href;
  const hrefStr = href == null ? "" : String(href).trim();
  if (!hrefStr || hrefStr === "undefined" || hrefStr === "null") {
    el.remove();
    return;
  }

  Object.entries(attributes).forEach(([k, v]) => {
    if (v == null) return;
    el.setAttribute(k, String(v));
  });
}

function removeLinkTag(selector) {
  document.head.querySelector(selector)?.remove();
}

// ─────────────────────────────────────────────────────────────────────────────
// GENERAL HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function formatCategoryLabel(slug) {
  return slug.split("-").filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function buildSeoDescription(label) {
  return `Explore ${label} travel inspiration, destination ideas, and curated stories designed to help you plan memorable journeys.`;
}

function buildLatestUpdatesSeoDescription() {
  return "Browse the latest travel blogs, new stories, and fresh editorial updates from Travel Rethink Ways.";
}

function buildSearchSeoDescription(query) {
  const normalized = String(query || "").trim();
  return normalized
    ? `Search results for "${normalized}" from Travel Rethink Ways travel stories, destination guides, and latest articles.`
    : "Search travel stories, destination guides, and latest articles from Travel Rethink Ways.";
}

function isSearchPath(pathname = window.location.pathname) {
  return /\/search\/?$/i.test(pathname);
}

function isLegacySearchPath(pathname = window.location.pathname, search = window.location.search) {
  return pathname === "/" && Boolean(getSearchQueryFromSearch(search));
}

function getPostCategoryTerms(post) {
  const termGroups = post?._embedded?.["wp:term"];
  if (!Array.isArray(termGroups)) return [];
  return termGroups.flat()
    .filter((t) => t?.taxonomy === "category" && (t?.slug || t?.name));
}

function containsExcludedLatestUpdatesKeyword(value) {
  const normalizedValue = String(value || "").toLowerCase();
  return EXCLUDED_LATEST_UPDATES_KEYWORDS.some((keyword) =>
    normalizedValue.includes(keyword)
  );
}

function filterLatestUpdatePosts(posts) {
  return posts.filter((post) => {
    const categoryTerms = getPostCategoryTerms(post);
    const hasExcludedCategory = categoryTerms.some((term) =>
      term?.slug === "brand-story" ||
      containsExcludedLatestUpdatesKeyword(term.slug) ||
      containsExcludedLatestUpdatesKeyword(term.name)
    );
    const title = post?.title?.rendered?.replace(/<[^>]+>/g, " ") || "";

    return !hasExcludedCategory && !containsExcludedLatestUpdatesKeyword(title);
  });
}

function filterSearchResultPosts(posts) {
  return posts.filter((post) => {
    const categoryTerms = getPostCategoryTerms(post);
    const hasExcludedCategory = categoryTerms.some((term) =>
      containsExcludedLatestUpdatesKeyword(term.slug) ||
      containsExcludedLatestUpdatesKeyword(term.name)
    );
    const title = post?.title?.rendered?.replace(/<[^>]+>/g, " ") || "";
    const excerpt = post?.excerpt?.rendered?.replace(/<[^>]+>/g, " ") || "";
    const link = String(post?.link || "");

    const normalizedQueryRaw = String(getSearchQueryFromSearch(window.location.search) || "").trim().toLowerCase();
    const normalizedQuery = normalizedQueryRaw.replace(/0/g, "o");
    const termGroups = post?._embedded?.["wp:term"];
    const tagTerms = Array.isArray(termGroups)
      ? termGroups.flat().filter((t) => t?.taxonomy === "post_tag" && (t?.name || t?.slug))
      : [];

    // Search page should only show posts whose *post tags* contain the query.
    // If a post has no post_tag terms, it should not appear.
    const prefixes = Array.from({ length: normalizedQuery.length }, (_, i) => normalizedQuery.slice(0, i + 1));
    const hasTagMatch =
      normalizedQuery.length >= 2 &&
      tagTerms.length > 0 &&
      tagTerms.some((t) => {
        const tagValue = String(t?.name || t?.slug || "").toLowerCase().replace(/0/g, "o");
        return prefixes.some((prefix) => tagValue.includes(prefix));
      });

    return (
      !hasExcludedCategory &&
      !containsExcludedLatestUpdatesKeyword(title) &&
      !containsExcludedLatestUpdatesKeyword(excerpt) &&
      !containsExcludedLatestUpdatesKeyword(link) &&
      hasTagMatch
    );
  });
}

function getPostsCacheKey(slug) { return `posts-cache:v3:${slug}`; }
function getSeoCacheKey(slug) { return `seo-cache:v1:${slug}`; }

function getSearchCacheKey(query) {
  return `search:${String(query || "").trim().toLowerCase()}`;
}

function readCachedPosts(slug) {
  try {
    const raw = window.localStorage.getItem(getPostsCacheKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.timestamp || Date.now() - parsed.timestamp > POSTS_CACHE_TTL_MS) {
      window.localStorage.removeItem(getPostsCacheKey(slug));
      return null;
    }
    return Array.isArray(parsed.posts) ? parsed.posts : null;
  } catch { return null; }
}

function writeCachedPosts(slug, posts) {
  try {
    window.localStorage.setItem(
      getPostsCacheKey(slug),
      JSON.stringify({ timestamp: Date.now(), posts })
    );
  } catch { /* ignore */ }
}

function readCachedSeo(slug) {
  try {
    const raw = window.localStorage.getItem(getSeoCacheKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.timestamp || Date.now() - parsed.timestamp > POSTS_CACHE_TTL_MS) {
      window.localStorage.removeItem(getSeoCacheKey(slug));
      return null;
    }
    return parsed.seo || null;
  } catch { return null; }
}

function writeCachedSeo(slug, seo) {
  try {
    window.localStorage.setItem(
      getSeoCacheKey(slug),
      JSON.stringify({ timestamp: Date.now(), seo })
    );
  } catch { /* ignore */ }
}

function getInitialArchiveRequest({ pageIsLatestUpdates, pageIsSearch, slug, searchQuery }) {
  if (pageIsLatestUpdates) {
    return fetchPosts({ orderby: "date", order: "desc", per_page: LATEST_UPDATES_PER_PAGE }).then((posts) => ({
      category: { name: LATEST_UPDATES_PAGE_TITLE, slug, link: "/latest-updates/" },
      posts: filterLatestUpdatePosts(Array.isArray(posts) ? posts : []),
    }));
  }
  if (pageIsSearch && slug === INITIAL_CATEGORY_SLUG && searchQuery === INITIAL_SEARCH_QUERY) {
    return INITIAL_POSTS_REQUEST.then((posts) => ({
      category: { name: SEARCH_RESULTS_PAGE_TITLE, slug, link: "" },
      posts: Array.isArray(posts) ? posts : [],
    }));
  }
  if (!pageIsLatestUpdates && slug === INITIAL_CATEGORY_SLUG) {
    return INITIAL_POSTS_REQUEST;
  }
  if (pageIsSearch) {
    if (!String(searchQuery || "").trim()) {
      return Promise.resolve({
        category: { name: SEARCH_RESULTS_PAGE_TITLE, slug, link: "" },
        posts: [],
      });
    }
    return fetchTagsBySearch(searchQuery, { per_page: 50 }).then((tags) => {
      const tagIds = (Array.isArray(tags) ? tags : []).map((t) => t?.id).filter(Boolean);
      if (!tagIds.length) {
        return { category: { name: SEARCH_RESULTS_PAGE_TITLE, slug, link: "" }, posts: [] };
      }

      return fetchPosts({ tags: tagIds.join(","), orderby: "date", order: "desc" }).then((posts) => ({
        category: { name: SEARCH_RESULTS_PAGE_TITLE, slug, link: "" },
        posts: filterSearchResultPosts(Array.isArray(posts) ? posts : []),
      }));
    });
  }
  // Try a single-call bundle endpoint (faster critical path) if present on WP,
  // otherwise fall back to the standard WP v2 calls.
  return fetchCategoryBundleBySlug(slug, { per_page: POSTS_PER_PAGE })
    .then((bundle) => {
      if (bundle?.category && Array.isArray(bundle?.posts)) return bundle;
      return fetchCategoryArchiveBySlug(slug);
    })
    .catch(() => fetchCategoryArchiveBySlug(slug));
}


// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

function CategoryPage() {
  const { slug: routeSlug } = useParams();
  const location = useLocation();

  const [posts,               setPosts]               = useState(() => readCachedPosts(INITIAL_CATEGORY_SLUG) || []);
  const [isLatestUpdatesPage, setIsLatestUpdatesPage] = useState(INITIAL_PAGE_IS_LATEST_UPDATES);
  const [isSearchResultsPage, setIsSearchResultsPage] = useState(INITIAL_PAGE_IS_SEARCH);
  const [categorySlug,        setCategorySlug]        = useState(INITIAL_CATEGORY_SLUG);
  const [searchQuery,         setSearchQuery]         = useState(INITIAL_SEARCH_QUERY);
  const [category,            setCategory]            = useState(null);
  const [seo,                 setSeo]                 = useState(INITIAL_SEO);
  const [loading,             setLoading]             = useState(false);
  const [error,               setError]               = useState("");
  const [page,                setPage]                = useState(1);
  const [hasMore,             setHasMore]             = useState(true);
  const isLoadingMoreRef                              = useRef(false);
  const loadedPagesRef                                = useRef(new Set());
  const [showBookmarkedOnly,  setShowBookmarkedOnly]  = useState(false);
  const [bookmarkedIds,       setBookmarkedIds]       = useState(() => readBookmarkedPostIds());
  const [bookmarkedPosts,     setBookmarkedPosts]     = useState(() => readBookmarkedPosts());

  // ── Sync bookmarks ──────────────────────────────────────────────────────────
  useEffect(() => {
    const sync = () => {
      setBookmarkedIds(readBookmarkedPostIds());
      setBookmarkedPosts(readBookmarkedPosts());
    };
    window.addEventListener("storage",  sync);
    window.addEventListener("pageshow", sync);
    window.addEventListener("focus",    sync);
    return () => {
      window.removeEventListener("storage",  sync);
      window.removeEventListener("pageshow", sync);
      window.removeEventListener("focus",    sync);
    };
  }, []);

  // ── Visible posts ───────────────────────────────────────────────────────────
  const visiblePosts = showBookmarkedOnly
    ? bookmarkedPosts.filter((post) =>
        bookmarkedIds.includes(String(post.__bookmarkId ?? post.id ?? post.slug ?? post.link ?? ""))
      )
    : posts;

  // ── Hydrate missing bookmarked posts ────────────────────────────────────────
  useEffect(() => {
    const savedPosts   = readBookmarkedPosts();
    const savedIds     = readBookmarkedPostIds();
    const savedPostIds = savedPosts.map((p) =>
      String(p.__bookmarkId ?? p.id ?? p.slug ?? p.link ?? "")
    );
    const missingIds = savedIds.filter((id) => !savedPostIds.includes(String(id)));
    if (!missingIds.length) return;

    let ignore = false;
    async function hydrate() {
      try {
        const fetched = await fetchPostsByIds(missingIds);
        if (ignore || !Array.isArray(fetched) || !fetched.length) return;
        const getId  = (e) => String(e.__bookmarkId ?? e.id ?? e.slug ?? e.link ?? "");
        const merged = [...savedPosts];
        fetched.forEach((post) => {
          const bmId = String(post.id ?? post.slug ?? post.link ?? "");
          if (!merged.some((e) => getId(e) === bmId)) merged.push({ ...post, __bookmarkId: bmId });
        });
        writeBookmarkedPosts(merged);
        setBookmarkedPosts(merged);
      } catch { /* keep local data */ }
    }
    void hydrate();
    return () => { ignore = true; };
  }, [bookmarkedIds]);

  // ── Push resolved SEO into <head> ───────────────────────────────────────────


  // ── LCP preload ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const selector = 'link[data-trw-lcp-preload="true"]';

    // Avoid noisy preload warnings in dev.
    if (import.meta.env.DEV) { removeLinkTag(selector); return; }

    const image = visiblePosts[0] ? getPostImageData(visiblePosts[0]) : null;
    const src = String(image?.src || "").trim();
    if (!src) { removeLinkTag(selector); return; }

    let href;
    try {
      href = new URL(src, window.location.href).toString();
    } catch {
      removeLinkTag(selector);
      return;
    }

    ensureLinkTag(selector, {
      rel: "preload",
      as: "image",
      href,
      fetchpriority: "high",
      "data-trw-lcp-preload": "true",
    });
  }, [visiblePosts]);

  // ── Fetch posts + SEO on route slug change ───────────────────────────────────
  useEffect(() => {
    let ignore = false;

    const pageIsLatestUpdates = isLatestUpdatesPath();
    const pageIsSearch = isSearchPath(location.pathname) || isLegacySearchPath(location.pathname, location.search);
    const nextSearchQuery = pageIsSearch
      ? getSearchQueryFromSearch(location.search)
      : "";
    const slug = pageIsLatestUpdates
      ? LATEST_UPDATES_SLUG
      : pageIsSearch
        ? getSearchCacheKey(nextSearchQuery)
        : (routeSlug || getCategorySlugFromPath());

    const cachedPosts = readCachedPosts(slug);

    // Reset for new slug
    setIsLatestUpdatesPage(pageIsLatestUpdates);
    setIsSearchResultsPage(pageIsSearch);
    setCategorySlug(slug);
    setSearchQuery(nextSearchQuery);
    setPage(1);
    setHasMore(true);
    isLoadingMoreRef.current = false;
    loadedPagesRef.current   = new Set();
    if (pageIsLatestUpdates || pageIsSearch) {
      setSeo(null);
    } else {
      setSeo(readCachedSeo(slug));
    }
    setCategory(pageIsLatestUpdates
      ? { name: LATEST_UPDATES_PAGE_TITLE, slug, link: "/latest-updates/" }
      : pageIsSearch
        ? { name: SEARCH_RESULTS_PAGE_TITLE, slug, link: "" }
        : null
    );
    setPosts(cachedPosts?.length ? cachedPosts : []);

    // Provisional title — one clean update before API responds
    const fallbackLabel = pageIsLatestUpdates
      ? LATEST_UPDATES_PAGE_TITLE
      : pageIsSearch
        ? (nextSearchQuery ? `${SEARCH_RESULTS_PAGE_TITLE}: ${nextSearchQuery}` : SEARCH_RESULTS_PAGE_TITLE)
        : formatCategoryLabel(slug);
    if (pageIsLatestUpdates || pageIsSearch) {
      document.title = `${fallbackLabel} | ${SEO_SITE_NAME}`;
    }

    async function load() {
      setLoading(!cachedPosts?.length);
      setError("");

      try {
        // Posts + SEO fetched in parallel.
        // fetchCategorySeoBySlug from api/posts uses the Vite proxy in dev
        // and the production URL in prod — no hardcoded URLs here.
        const archiveResult = await getInitialArchiveRequest({
          pageIsLatestUpdates,
          pageIsSearch,
          slug,
          searchQuery: nextSearchQuery,
        });

        const categorySeoResult = pageIsLatestUpdates || pageIsSearch
          ? null
          : await (async () => {
              const baseSeo = await fetchCategorySeoBySlug(slug);
              if (!baseSeo) return null;

              // Prefer Rank Math term SEO when available (even if Yoast fields exist),
              // because the user expects Rank Math to drive category SEO.
              try {
                const rm = await fetchRankMathTermSeo({
                  taxonomy: "category",
                  id: archiveResult?.category?.id,
                  slug,
                });
                const hasRmSeo = Boolean(rm?.title || rm?.description || rm?.canonicalUrl || rm?.image);
                if (hasRmSeo) {
                  return {
                    ...baseSeo,
                    title: rm?.title || baseSeo.title,
                    description: rm?.description || baseSeo.description,
                    canonicalUrl: rm?.canonicalUrl || baseSeo.canonicalUrl,
                    image: rm?.image || baseSeo.image,
                  };
                }
              } catch {
                // ignore and fall back
              }

              return baseSeo;
            })();

        if (ignore) return;

        // Posts
        setCategory(archiveResult.category);
        setPosts(archiveResult.posts);
        writeCachedPosts(slug, archiveResult.posts);
        loadedPagesRef.current.add(1);
        setHasMore(archiveResult.posts.length > 0);

        // SEO — resolve through Rank Math → Yoast → fallback chain
        if (pageIsLatestUpdates) {
          setSeo({
            title:        `${LATEST_UPDATES_PAGE_TITLE} | ${SEO_SITE_NAME}`,
            description:  buildLatestUpdatesSeoDescription(),
            canonicalUrl: `${window.location.origin}/latest-updates/`,
            image:        getPostImageUrl(archiveResult.posts[0]) || DEFAULT_SEO_IMAGE,
          });
        } else if (pageIsSearch) {
          setSeo({
            title: nextSearchQuery
              ? `${SEARCH_RESULTS_PAGE_TITLE}: ${nextSearchQuery} | ${SEO_SITE_NAME}`
              : `${SEARCH_RESULTS_PAGE_TITLE} | ${SEO_SITE_NAME}`,
            description: buildSearchSeoDescription(nextSearchQuery),
            canonicalUrl: `${window.location.origin}/search${nextSearchQuery ? `?q=${encodeURIComponent(nextSearchQuery)}` : ""}`,
            image: getPostImageUrl(archiveResult.posts[0]) || DEFAULT_SEO_IMAGE,
          });
        } else {
          const resolvedLabel = archiveResult.category?.name || formatCategoryLabel(slug);
          const nextSeo = resolveCategorySeo(categorySeoResult, resolvedLabel);
          setSeo(nextSeo);
          writeCachedSeo(slug, nextSeo);
        }
      } catch (err) {
        if (!ignore) {
          if (!cachedPosts?.length) setPosts([]);
          setError(err.message || "Unable to load posts.");
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();
    return () => { ignore = true; };
  }, [location.pathname, location.search, routeSlug]);

  // ── Infinite scroll load more ───────────────────────────────────────────────
  const loadMore = useCallback(async () => {
    if (showBookmarkedOnly || loading || isLoadingMoreRef.current || !hasMore) return;
    const nextPage = page + 1;
    if (loadedPagesRef.current.has(nextPage)) return;

    isLoadingMoreRef.current = true;
    setError("");

    try {
      const slug      = isLatestUpdatesPage ? LATEST_UPDATES_SLUG : categorySlug;
      const isLatest  = isLatestUpdatesPage;
      const nextPosts = isLatest
        ? await fetchPosts({ orderby: "date", order: "desc", page: nextPage, per_page: LATEST_UPDATES_PER_PAGE }).then(filterLatestUpdatePosts)
        : isSearchResultsPage
          ? (searchQuery
              ? await fetchTagsBySearch(searchQuery, { per_page: 50 }).then(async (tags) => {
                  const tagIds = (Array.isArray(tags) ? tags : []).map((t) => t?.id).filter(Boolean);
                  if (!tagIds.length) return [];
                  const posts = await fetchPosts({ tags: tagIds.join(","), orderby: "date", order: "desc", page: nextPage });
                  return filterSearchResultPosts(Array.isArray(posts) ? posts : []);
                })
              : [])
          : (await fetchCategoryArchiveBySlug(slug, { page: nextPage })).posts;

      loadedPagesRef.current.add(nextPage);
      setPosts((cur) => {
        const merged = [...cur, ...nextPosts];
        writeCachedPosts(slug, merged);
        return merged;
      });
      setPage(nextPage);
      setHasMore(nextPosts.length > 0);
    } catch (err) {
      // WordPress returns 400 (rest_post_invalid_page_number) when `page`
      // exceeds the last page. Treat that as "no more posts" and stop
      // infinite scroll without showing an error.
      if (err?.status === 400) {
        setHasMore(false);
        setError("");
        return;
      }

      setHasMore(false);
      setError(err?.message || "Unable to load more posts.");
    } finally {
      isLoadingMoreRef.current = false;
    }
  }, [categorySlug, hasMore, isLatestUpdatesPage, isSearchResultsPage, loading, page, searchQuery, showBookmarkedOnly]);

  useEffect(() => {
    const onScroll = () => {
      if (showBookmarkedOnly || loading || !hasMore) return;
      const scrollBottom = window.innerHeight + window.scrollY;
      const pageHeight   = document.documentElement.scrollHeight;
      if (scrollBottom < pageHeight - LOAD_MORE_THRESHOLD_PX) return;
      loadMore();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [hasMore, loadMore, loading, showBookmarkedOnly]);

  // ── Derived labels ──────────────────────────────────────────────────────────
  const pageLabel    = isLatestUpdatesPage
    ? LATEST_UPDATES_PAGE_TITLE
    : isSearchResultsPage
      ? (searchQuery ? `Search Results: ${searchQuery}` : SEARCH_RESULTS_PAGE_TITLE)
      : (category?.name || formatCategoryLabel(categorySlug));
  const categoryLink = isLatestUpdatesPage
    ? ""
    : isSearchResultsPage
      ? ""
    : (category?.link || `/category/${categorySlug}/`);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="trw-app min-h-screen bg-white transition-colors duration-200 [.trw-dark-mode_&]:bg-[#000]">
      {seo && (
        <SEO
          title={seo.title}
          description={seo.description}
          canonical={seo.canonicalUrl}
          image={seo.image}
          siteName={SEO_SITE_NAME}
        />
      )}
      <main
        className="archive-page relative mx-auto max-w-[1500px] overflow-hidden px-8 pb-[52px] pt-8 text-[16px] leading-6 text-[rgb(91,95,98)] [.trw-dark-mode_&]:bg-black [.trw-dark-mode_&]:text-[#cbd5e1]"
        style={{ fontFamily: "'Yeseva One', Georgia, serif" }}
      >
        {/* Visually-hidden SEO h1 */}
        <h1
          className="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0"
          style={{ clip: "rect(0,0,0,0)" }}
        >
          {pageLabel}
        </h1>

        {error && (
          <p className="mt-1.5 text-base font-normal leading-6 text-[#b42318]">
            {error}
          </p>
        )}

        <PostGrid
          posts={visiblePosts}
          loading={showBookmarkedOnly ? false : loading}
          categoryLabel={isSearchResultsPage ? "" : pageLabel}
          categorySlug={isSearchResultsPage ? "" : (isLatestUpdatesPage ? "" : (category?.slug || categorySlug))}
          categoryLink={isSearchResultsPage ? "" : categoryLink}
          showCategoryPills={false}
          showCategoryTag={true}
          mixedCategories={showBookmarkedOnly || isLatestUpdatesPage || isSearchResultsPage}
          showInlineAd={!showBookmarkedOnly && !isSearchResultsPage}
          inlineAdEveryRows={2}
        />
      </main>
    </div>
  );
}

export default CategoryPage;
